import { Currency } from '@/api/common';
import { Country } from '@/helpers/conf';
import { License } from '@/store/commonStore/types';

export enum CurrencyCode {
  // FIAT
  EUR = 'EUR',
  USD = 'USD',
  CAD = 'CAD',
  AUD = 'AUD',
  NOK = 'NOK',
  RUB = 'RUB',
  PLN = 'PLN',
  NZD = 'NZD',
  JPY = 'JPY',
  BRL = 'BRL',
  IDR = 'IDR',
  INR = 'INR',
  KRW = 'KRW',
  MYR = 'MYR',
  PHP = 'PHP',
  THB = 'THB',
  VND = 'VND',
  ZAR = 'ZAR',
  // CRYPTO
  BTC = 'BTC',
  MBTC = 'mBTC',
  BCH = 'BCH',
  ETH = 'ETH',
  LTC = 'LTC',
  DOG = 'DOG',
  USDT = 'USDT',
  USDC = 'USDC',
}

export const mapLocaleCurrencyCode: { [locale: string]: string } = {
  en: CurrencyCode.USD,
  de: CurrencyCode.EUR,
  'en-CA': CurrencyCode.CAD,
  'fr-CA': CurrencyCode.CAD,
  'fr-CH': CurrencyCode.EUR,
  it: CurrencyCode.EUR,
  nn: CurrencyCode.NOK,
  no: CurrencyCode.NOK,
  pt: CurrencyCode.EUR,
  'pt-BR': CurrencyCode.BRL,
  'en-AU': CurrencyCode.AUD,
  'en-IE': CurrencyCode.EUR,
  'en-NZ': CurrencyCode.NZD,
  sl: CurrencyCode.EUR,
  'et-EE': CurrencyCode.EUR,
};

export const currencyFlags: {
  [code: string]: string;
} = {
  [CurrencyCode.EUR]: '/currency/EUR.svg',
  [CurrencyCode.USD]: '/currency/USD.svg',
  [CurrencyCode.CAD]: '/currency/CAD.svg',
  [CurrencyCode.AUD]: '/currency/AUD.svg',
  [CurrencyCode.NOK]: '/currency/NOK.svg',
  [CurrencyCode.RUB]: '/currency/RUB.svg',
  [CurrencyCode.PLN]: '/currency/PLN.svg',
  [CurrencyCode.NZD]: '/currency/NZD.svg',
  [CurrencyCode.JPY]: '/currency/JPY.svg',
  [CurrencyCode.BRL]: '/currency/BRL.svg',
  [CurrencyCode.BTC]: '/currency/BTC.svg',
  [CurrencyCode.BCH]: '/currency/BCH.svg',
  [CurrencyCode.ETH]: '/currency/ETH.svg',
  [CurrencyCode.LTC]: '/currency/LTC.svg',
  [CurrencyCode.DOG]: '/currency/DOG.svg',
  [CurrencyCode.USDT]: '/currency/USDT.svg',
  [CurrencyCode.USDC]: '/currency/USDC.svg',
  [CurrencyCode.IDR]: '/currency/IDR.svg',
  [CurrencyCode.INR]: '/currency/INR.svg',
  [CurrencyCode.KRW]: '/currency/KRW.svg',
  [CurrencyCode.MYR]: '/currency/MYR.svg',
  [CurrencyCode.PHP]: '/currency/PHP.svg',
  [CurrencyCode.THB]: '/currency/THB.svg',
  [CurrencyCode.VND]: '/currency/VND.svg',
  [CurrencyCode.ZAR]: '/currency/ZAR.svg',
};

export const mapCurrencySymbol: { [code: string]: string } = {
  [CurrencyCode.EUR]: '€{}',
  [CurrencyCode.USD]: '${}',
  [CurrencyCode.CAD]: 'C${}',
  [CurrencyCode.AUD]: 'A${}',
  [CurrencyCode.NOK]: '{}kr',
  [CurrencyCode.RUB]: '{}rub',
  [CurrencyCode.PLN]: '{}zl',
  [CurrencyCode.NZD]: 'NZ${}',
  [CurrencyCode.JPY]: '{} JPY',
  [CurrencyCode.BRL]: 'R${}',
  [CurrencyCode.ZAR]: 'R{}',
  [CurrencyCode.BTC]: '{} BTC',
  [CurrencyCode.BCH]: '{} BCH',
  [CurrencyCode.ETH]: '{} ETH',
  [CurrencyCode.LTC]: '{} LTC',
  [CurrencyCode.DOG]: '{} DOGE',
  [CurrencyCode.USDT]: '{} USDT',
  [CurrencyCode.MBTC]: '{} mBTC',
};

export const ALL_FIAT = [
  CurrencyCode.EUR,
  CurrencyCode.USD,
  CurrencyCode.CAD,
  CurrencyCode.AUD,
  CurrencyCode.NOK,
  CurrencyCode.RUB,
  CurrencyCode.PLN,
  CurrencyCode.NZD,
  CurrencyCode.JPY,
  CurrencyCode.BRL,
  CurrencyCode.IDR,
  CurrencyCode.INR,
  CurrencyCode.KRW,
  CurrencyCode.MYR,
  CurrencyCode.PHP,
  CurrencyCode.THB,
  CurrencyCode.VND,
  CurrencyCode.ZAR,
];

export const ALL_CRYPTO = [
  CurrencyCode.BTC,
  CurrencyCode.BCH,
  CurrencyCode.ETH,
  CurrencyCode.LTC,
  CurrencyCode.DOG,
  CurrencyCode.USDT,
];

export const mapCurrencyName: {
  [code: string]: string;
} = {
  // FIAT
  [CurrencyCode.EUR]: 'Euro',
  [CurrencyCode.USD]: 'United States Dollar',
  [CurrencyCode.CAD]: 'Canadian Dollar',
  [CurrencyCode.AUD]: 'Australian Dollar',
  [CurrencyCode.NOK]: 'Norwegian krone',
  [CurrencyCode.RUB]: 'Russian Ruble',
  [CurrencyCode.PLN]: 'Polish Złoty',
  [CurrencyCode.NZD]: 'New Zealand Dollar',
  [CurrencyCode.JPY]: 'Japanese Yen',
  [CurrencyCode.BRL]: 'Brazilian Real',
  [CurrencyCode.ZAR]: 'South African Rand',
  // CRYPTO
  [CurrencyCode.BTC]: 'Bitcoin',
  [CurrencyCode.BCH]: 'Bitcoin Cash',
  [CurrencyCode.ETH]: 'Ethereum',
  [CurrencyCode.LTC]: 'Litecoin',
  [CurrencyCode.DOG]: 'Dogecoin',
  [CurrencyCode.USDT]: 'Tether',
};

export const MAP_COUNTRY_TO_CURRENCY: { [country: string]: string } = {
  [Country.Australia]: CurrencyCode.AUD,
  [Country.NewZealand]: CurrencyCode.NZD,
  [Country.Canada]: CurrencyCode.CAD,
  [Country.Norway]: CurrencyCode.NOK,
  [Country.Brazil]: CurrencyCode.BRL,
  [Country.SouthAfrica]: CurrencyCode.ZAR,
  [Country.Germany]: CurrencyCode.EUR,
  [Country.Estonia]: CurrencyCode.EUR,
  [Country.Mexico]: CurrencyCode.BTC,
  [Country.Argentina]: CurrencyCode.BTC,
  [Country.Uruguay]: CurrencyCode.BTC,
  [Country.Chile]: CurrencyCode.BTC,
  [Country.PuertoRico]: CurrencyCode.BTC,
  [Country.Peru]: CurrencyCode.BTC,
};

export const mapCurrencyRestrictions: { [country: string]: string[] } = {
  [Country.Mexico]: ALL_FIAT,
  [Country.Argentina]: ALL_FIAT,
  [Country.Uruguay]: ALL_FIAT,
  [Country.Chile]: ALL_FIAT,
  [Country.PuertoRico]: ALL_FIAT,
  [Country.Peru]: ALL_FIAT,
  [Country.Estonia]: ALL_CRYPTO,
};

export const MAP_CURRENCY_BY_LICENSE: { [country: string]: string[] } = {
  [License.EE]: ALL_CRYPTO,
};

export const currencyNameMap: Record<string, string> = {
  [CurrencyCode.LTC]: 'mLTC',
  [CurrencyCode.DOG]: 'DOGE',
  [CurrencyCode.BTC]: 'mBTC',
  [CurrencyCode.BCH]: 'mBCH',
  [CurrencyCode.ETH]: 'mETH',
};

export const CurrencyLimit: Record<string, number> = {
  [CurrencyCode.CAD]: 1000,
  [CurrencyCode.AUD]: 1000,
  [CurrencyCode.EUR]: 1000,
  [CurrencyCode.NZD]: 1000,
  [CurrencyCode.USD]: 1000,
  [CurrencyCode.NOK]: 10000,
  [CurrencyCode.ZAR]: 20000,
  [CurrencyCode.BRL]: 10000,
  [CurrencyCode.BTC]: 20,
  [CurrencyCode.BCH]: 2250,
  [CurrencyCode.ETH]: 700,
  [CurrencyCode.LTC]: 6000,
  [CurrencyCode.DOG]: 4000,
  [CurrencyCode.USDT]: 1000,
};

export const getCurrencyName = (currency: string) => {
  return currencyNameMap[currency] ?? currency;
};

export const decimalPlacesMapCrypto: Record<string, number> = {
  [currencyNameMap[CurrencyCode.BTC]]: 4,
  [currencyNameMap[CurrencyCode.ETH]]: 3,
  [currencyNameMap[CurrencyCode.LTC]]: 2,
  [currencyNameMap[CurrencyCode.DOG]]: 2,
  [currencyNameMap[CurrencyCode.BCH]]: 3,
};

export const fixedAmount = (
  amount: number,
  currencyCode: string,
  decimalPlaces?: number
) => {
  const decimalPlacesByCurrency =
    decimalPlacesMapCrypto[currencyCode] || decimalPlaces;

  return amount ? amount.toFixed(decimalPlacesByCurrency) : '0';
};

interface AmountWithCurrencySymbolOptions {
  wholeRounding?: boolean;
}

export const amountWithCurrencySymbol = (
  currencyCode: string | undefined,
  amount: number | string,
  options?: AmountWithCurrencySymbolOptions
) => {
  if (currencyCode == null) return amount.toString();

  const mask = mapCurrencySymbol[currencyCode] || null;
  const hasComma = typeof amount === 'string' && amount.includes(',');
  const isWhole = options?.wholeRounding && Number.isInteger(+amount);
  const amountDecimalPlaces = isWhole ? 0 : 2;

  const formattedAmount = hasComma
    ? amount
    : fixedAmount(Number(amount), currencyCode, amountDecimalPlaces);

  if (!mask) return `${formattedAmount} ${currencyCode}`;

  const result = mask.replace('{}', formattedAmount);
  return result;
};

export const getCurrencyCodeByLocation = (countryCode?: string) => {
  if (countryCode) {
    return MAP_COUNTRY_TO_CURRENCY[countryCode] || CurrencyCode.EUR;
  }
  return CurrencyCode.AUD;
};

export const getCurrenciesByCountry = (
  currencies: Currency[],
  countryCode: string
) => {
  const unavailableCurrencies = mapCurrencyRestrictions[countryCode] || [];
  return currencies.filter(({ code }) => !unavailableCurrencies.includes(code));
};

export const getCurrenciesByLicense = (
  currencies: Currency[],
  license: string
) => {
  const unavailableCurrencies = MAP_CURRENCY_BY_LICENSE[license] || [];
  return currencies.filter(({ code }) => !unavailableCurrencies.includes(code));
};

export const isCryptoCurrency = (
  currencies: Currency[],
  currencyCode: string
) =>
  currencies?.some(
    (currency) => currency.code === currencyCode && !currency.fiat
  );

export const DEFAULT_CURRENCIES: Currency[] = [
  {
    code: CurrencyCode.AUD,
    symbol: 'A$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.EUR,
    symbol: '€',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.USD,
    symbol: '$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BTC,
    symbol: 'BTC',
    subunitsToUnit: 100000000,
    fiat: false,
    subcurrencies: [
      {
        code: CurrencyCode.MBTC,
        symbol: 'mBTC',
        subunitsToUnit: 100000,
      },
      {
        code: 'μBTC',
        symbol: 'μBTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.NZD,
    symbol: 'NZ$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.CAD,
    symbol: 'C$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.ETH,
    symbol: 'ETH',
    subunitsToUnit: 1000000000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mETH',
        symbol: 'mETH',
        subunitsToUnit: 1000000,
      },
      {
        code: 'μETH',
        symbol: 'μETH',
        subunitsToUnit: 1000,
      },
    ],
  },
  {
    code: CurrencyCode.USDT,
    symbol: 'USDT',
    subunitsToUnit: 100000000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.NOK,
    symbol: 'kr',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.LTC,
    symbol: 'LTC',
    subunitsToUnit: 100000000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mLTC',
        symbol: 'mLTC',
        subunitsToUnit: 100000,
      },
      {
        code: 'μLTC',
        symbol: 'μLTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.DOG,
    symbol: 'DOGE',
    subunitsToUnit: 100000000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BRL,
    symbol: 'R$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BCH,
    symbol: 'BCH',
    subunitsToUnit: 100000000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mBCH',
        symbol: 'mBCH',
        subunitsToUnit: 100000,
      },
      {
        code: 'μBCH',
        symbol: 'μBCH',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.ZAR,
    symbol: 'R',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
];
